import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { type FocusEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type DecisionGroupView,
  type DecisionSheetView,
  MAX_DECISION_GROUPS,
  MAX_DECISION_QUESTIONS,
  nextDecisionCode,
  UpdateDecisionSheetInput,
  UpsertDecisionGroupInput,
} from '@apm/contracts';
import { useConfirmPopover } from '../../../components/ui/confirm-popover';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { randomUuid } from '../../../lib/uuid';
import { decisionSheetApi, decisionSheetKeys } from '../api';
import {
  draftTempId,
  editorRows,
  isDraftRow,
  isGroupRow,
  isGroupTail,
  nextGroupCode,
  reorderInput,
  UNGROUPED_ROW,
  withDraftRows,
} from '../editor-order';
import { decisionSections } from '../model';
import { InlineText } from './inline-text';
import { SheetStatusPill } from './labels';
import { CodeChip, QuestionCardEditor, SaveState } from './question-card-editor';

/** Câu mới chưa có trên server — một thẻ trống ở cuối nhóm của nó. */
interface NewQuestion {
  readonly tempId: string;
  readonly groupId: string | null;
  readonly code: string;
}

const HEADER_REVEAL =
  'opacity-0 group-hover/header:opacity-100 focus-visible:opacity-100 max-sm:opacity-100 pointer-coarse:opacity-100';

/**
 * Nhận bản server khi nó đổi — trừ khi người dùng đang sửa dở (bản nháp đã
 * khác bản server trước đó). Cùng cách ghi chú của thẻ chế độ Xem.
 */
function useSyncedDraft<T>(server: T, keyOf: (value: T) => string) {
  const [draft, setDraft] = useState(server);
  const serverKey = keyOf(server);
  const [seen, setSeen] = useState(serverKey);
  if (serverKey !== seen) {
    setSeen(serverKey);
    if (keyOf(draft) === seen) setDraft(server);
  }
  return [draft, setDraft, serverKey] as const;
}

/**
 * Vỏ sổ trong chế độ Sửa — APM-DSX-e: tên, mô tả sửa **tại chỗ** ở đúng chỗ
 * chế độ Xem đặt chúng (đầu trang), cộng công tắc "Phương án khác".
 * Mỗi ô lưu khi rời ô — chỉ gửi trường thực sự đổi (`UpdateDecisionSheet`).
 *
 * "Xoá sổ" không ở đây nữa (người dùng 2026-10-07): nó là một nút ở hàng nút
 * đầu trang (`DecisionSheetPage`), hiện ở cả Xem lẫn Sửa và mọi trạng thái —
 * sổ đã đóng không vào được Sửa, nên nút nằm ở đây thì sổ đóng không xoá được.
 */
export function SheetMetaEditor({ view }: { readonly view: DecisionSheetView }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useSyncedDraft(view.title, String);
  const [description, setDescription] = useSyncedDraft(view.description ?? '', String);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['decision-sheet', view.id] });
    void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(view.projectId) });
  };
  const update = useMutation({
    mutationFn: (input: UpdateDecisionSheetInput) => decisionSheetApi.update(view.id, input),
    onSuccess: refresh,
    onError: (error) => {
      toast.error(errorMessage(error, t));
      refresh();
    },
  });
  const saveMeta = (patch: UpdateDecisionSheetInput): void => {
    const parsed = UpdateDecisionSheetInput.safeParse(patch);
    if (parsed.success) update.mutate(parsed.data);
  };

  return (
    <div className="flex flex-col gap-2" data-testid="decision-sheet-meta-editor">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1 basis-60">
          <InlineText
            aria-label={t('decisionSheet.editor.sheetTitle')}
            placeholder={t('decisionSheet.editor.sheetTitle')}
            value={title}
            maxLength={300}
            onValueChange={setTitle}
            onBlur={() => {
              const next = title.trim();
              // Tên là bắt buộc: xoá trắng rồi rời ô ⇒ về tên cũ, không gửi.
              if (next === '') setTitle(view.title);
              else if (next !== view.title) saveMeta({ title: next });
            }}
            className="text-xl font-semibold leading-7"
          />
        </div>
        <span className="mt-1.5">
          <SheetStatusPill status={view.status} />
        </span>
      </div>
      <InlineText
        multiline
        aria-label={t('decisionSheet.editor.sheetDescription')}
        placeholder={t('decisionSheet.editor.sheetDescriptionHint')}
        value={description}
        maxLength={5_000}
        onValueChange={setDescription}
        onBlur={() => {
          const next = description.trim();
          if (next !== (view.description ?? '')) {
            saveMeta({ description: next === '' ? null : next });
          }
        }}
        className="text-sm text-muted-foreground"
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="flex min-h-10 items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="ios-switch"
            checked={view.allowOther}
            disabled={update.isPending}
            onChange={(event) => saveMeta({ allowOther: event.target.checked })}
          />
          {t('decisionSheet.editor.allowOther')}
        </label>
      </div>
    </div>
  );
}

/**
 * Trình soạn sổ — chế độ **Sửa** của trang sổ. APM-DSHEET-d, làm lại ở
 * APM-DSX-e (plan §11.4): cùng bố cục với chế độ Xem — tiêu đề nhóm rồi các
 * thẻ câu — nhưng mọi chữ sửa tại chỗ. Không còn hộp thoại sửa câu / nhóm.
 *
 * Mỗi câu lưu bằng **một** lệnh `UpsertDecisionQuestion` khi rời thẻ hoặc
 * ngừng gõ (`QuestionCardEditor`); mỗi nhóm một `UpsertDecisionGroup` khi rời
 * tiêu đề nhóm. Không có "Lưu tất cả": mỗi lệnh một event, và một nút lưu
 * chung nghĩa là một thay đổi lớn không ai đọc nổi trong Lịch sử.
 *
 * Kéo thả bằng tay cầm bên trái thẻ — thứ tự vẫn là danh sách phẳng của
 * `editor-order.ts` (tiêu đề nhóm là mốc, câu thuộc tiêu đề gần nhất phía trên).
 *
 * Sổ đang `open`: chặn ở giao diện **đúng** các ca server chặn, ngay trên ô
 * liên quan (xem `QuestionCardEditor`).
 *
 * Xoá nhóm chỉ khi nhóm rỗng: kéo câu ra trước. Server có thể cho xoá nhóm có
 * câu (câu về "Chưa xếp nhóm"), nhưng một cú bấm làm đổi chỗ hai chục câu thì
 * nên là hai bước người dùng nhìn thấy.
 */
export function DecisionSheetEditor({ view }: { readonly view: DecisionSheetView }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const confirm = useConfirmPopover();
  const [drafts, setDrafts] = useState<readonly NewQuestion[]>([]);
  /** Câu mới đã lưu: id server ⇒ `tempId` — để thẻ giữ nguyên `key` (và tiêu điểm). */
  const [created, setCreated] = useState<ReadonlyMap<string, string>>(new Map());
  const [focusGroupId, setFocusGroupId] = useState<string | null>(null);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['decision-sheet', view.id] });
    void queryClient.invalidateQueries({ queryKey: decisionSheetKeys.list(view.projectId) });
  };
  const onError = (error: unknown): void => {
    toast.error(errorMessage(error, t));
    refresh();
  };

  const removeQuestion = useMutation({
    mutationFn: (questionId: string) => decisionSheetApi.removeQuestion(view.id, questionId),
    onSuccess: refresh,
    onError,
  });
  const removeGroup = useMutation({
    mutationFn: (groupId: string) => decisionSheetApi.removeGroup(view.id, groupId),
    onSuccess: refresh,
    onError,
  });
  const reorder = useMutation({
    mutationFn: (rows: readonly string[]) =>
      decisionSheetApi.reorderQuestions(view.id, reorderInput(rows)),
    onSuccess: refresh,
    onError,
  });

  const sections = decisionSections(view, 'all', true);
  const groups = [...view.groups].sort((a, b) => a.position - b.position);
  const groupCodes = groups.map((group) => group.code);
  const byId = new Map(view.questions.map((question) => [question.id, question]));
  const groupById = new Map(groups.map((group) => [group.id, group]));

  const realOfTemp = new Map([...created].map(([real, temp]) => [temp, real]));
  // Câu mới mà sổ đã có ⇒ thôi vẽ hàng nháp, thẻ thật thế chỗ ở cùng `key`.
  const pending = drafts.filter((draft) => {
    const real = realOfTemp.get(draft.tempId);
    return !(real && byId.has(real));
  });
  const draftByTemp = new Map(pending.map((draft) => [draft.tempId, draft]));
  const rows = withDraftRows(editorRows(view), pending);
  const orderedCodes = [
    ...sections.flatMap((section) => section.questions.map((question) => question.code)),
    ...pending.map((draft) => draft.code),
  ];
  const questionCount = view.questions.length + pending.length;

  const addGroup = useMutation({
    mutationFn: () =>
      decisionSheetApi.upsertGroup(view.id, {
        code: nextGroupCode(groupCodes),
        title: t('decisionSheet.editor.newGroupTitle'),
      }),
    onSuccess: (result) => {
      setFocusGroupId(result.id);
      refresh();
    },
    onError,
  });

  const addQuestion = (groupId: string | null): void =>
    setDrafts((previous) => [
      ...previous,
      { tempId: randomUuid(), groupId, code: nextDecisionCode(orderedCodes) },
    ]);
  const dropDraft = (tempId: string): void =>
    setDrafts((previous) => previous.filter((draft) => draft.tempId !== tempId));

  const deleteQuestion = (questionId: string | null, code: string, tempId?: string): void => {
    if (questionId === null) {
      if (tempId) dropDraft(tempId);
      return;
    }
    confirm.ask({
      message: t('decisionSheet.editor.deleteQuestionConfirm', { code }),
      onConfirm: () =>
        removeQuestion.mutate(questionId, {
          onSuccess: () => {
            if (tempId) dropDraft(tempId);
          },
        }),
    });
  };

  const addQuestionButton = (groupId: string | null): ReactNode => (
    <button
      type="button"
      disabled={questionCount >= MAX_DECISION_QUESTIONS}
      onClick={() => addQuestion(groupId)}
      data-testid={`decision-add-question-${groupId ?? 'ungrouped'}`}
      className="inline-flex min-h-10 items-center gap-1.5 self-start rounded-full px-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Plus aria-hidden className="size-4" />
      {t('decisionSheet.editor.addQuestion')}
    </button>
  );

  const ungroupedCount = sections.find((section) => section.group === null)?.total ?? 0;

  return (
    <div className="flex flex-col gap-3" data-testid="decision-sheet-editor">
      {view.status === 'open' ? (
        <p
          role="note"
          className="rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/10 px-3 py-2 text-sm"
        >
          {t('decisionSheet.editor.openNotice')}
        </p>
      ) : null}

      <p className="text-xs text-muted-foreground">{t('decisionSheet.editor.dragHint')}</p>

      <SortableRows
        rows={rows}
        rowKey={(rowId) => (isDraftRow(rowId) ? draftTempId(rowId) : (created.get(rowId) ?? rowId))}
        disabled={reorder.isPending}
        onReorder={(next) => reorder.mutate(next)}
        renderRow={(rowId, index, current) => {
          const tail = isGroupTail(current, index);

          if (isGroupRow(rowId)) {
            const groupId = rowId === UNGROUPED_ROW ? null : rowId.slice('group:'.length);
            const group = groupId ? (groupById.get(groupId) ?? null) : null;
            const section = sections.find((entry) => (entry.group?.id ?? null) === groupId);
            const empty =
              (section?.total ?? 0) === 0 && !pending.some((draft) => draft.groupId === groupId);
            if (!group) {
              const showAdd = groups.length === 0 || ungroupedCount > 0;
              return {
                body: (
                  <p className="pt-3 text-sm font-semibold text-muted-foreground">
                    {t('decisionSheet.ungrouped')}
                  </p>
                ),
                footer: tail && showAdd ? addQuestionButton(null) : null,
              };
            }
            return {
              body: (
                <GroupHeaderEditor
                  key={group.id}
                  sheetId={view.id}
                  group={group}
                  empty={empty}
                  otherCodes={groupCodes.filter((code) => code !== group.code)}
                  autoFocus={focusGroupId === group.id}
                  onDelete={() =>
                    confirm.ask({
                      message: t('decisionSheet.editor.deleteGroupConfirm', { code: group.code }),
                      onConfirm: () => removeGroup.mutate(group.id),
                    })
                  }
                />
              ),
              footer: tail ? addQuestionButton(group.id) : null,
            };
          }

          const draft = isDraftRow(rowId) ? draftByTemp.get(draftTempId(rowId)) : undefined;
          const question = draft ? null : (byId.get(rowId) ?? null);
          if (!draft && !question) return { body: null, footer: null };
          const tempId = draft?.tempId ?? (question ? created.get(question.id) : undefined);
          const groupId = question?.groupId ?? draft?.groupId ?? null;
          return {
            body: (
              <QuestionCardEditor
                sheetId={view.id}
                projectId={view.projectId}
                status={view.status}
                allowOther={view.allowOther}
                question={question}
                newCode={draft?.code ?? question?.code ?? ''}
                newGroupId={draft?.groupId ?? null}
                otherCodes={view.questions
                  .filter((entry) => entry.id !== question?.id)
                  .map((entry) => entry.code)}
                autoFocus={draft !== undefined}
                onCreated={(id) => {
                  if (draft) setCreated((previous) => new Map(previous).set(id, draft.tempId));
                }}
                onDelete={(id, code) => deleteQuestion(id, code, tempId)}
              />
            ),
            footer: tail ? addQuestionButton(groupId) : null,
          };
        }}
      />

      <button
        type="button"
        disabled={addGroup.isPending || groups.length >= MAX_DECISION_GROUPS}
        onClick={() => addGroup.mutate()}
        className="flex min-h-11 items-center justify-center gap-2 rounded-DEFAULT border border-dashed border-border px-3 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus aria-hidden className="size-4" />
        {t('decisionSheet.editor.addGroup')}
      </button>

      {confirm.popover}
    </div>
  );
}

/**
 * Tiêu đề nhóm sửa tại chỗ — mã (nhãn đậm), tên, nhãn nổi bật, ghi chú; đúng
 * vị trí của chúng ở chế độ Xem. Lưu một `UpsertDecisionGroup` khi focus rời
 * tiêu đề nhóm và có gì đổi.
 */
function GroupHeaderEditor({
  sheetId,
  group,
  empty,
  otherCodes,
  autoFocus,
  onDelete,
}: {
  readonly sheetId: string;
  readonly group: DecisionGroupView;
  readonly empty: boolean;
  readonly otherCodes: readonly string[];
  readonly autoFocus: boolean;
  readonly onDelete: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const [touched, setTouched] = useState(false);

  const server = {
    code: group.code,
    title: group.title,
    note: group.note ?? '',
    highlight: group.highlight ?? '',
  };
  const keyOf = (value: typeof server): string =>
    JSON.stringify([
      value.code.trim(),
      value.title.trim(),
      value.note.trim(),
      value.highlight.trim(),
    ]);
  const [draft, setDraft, serverKey] = useSyncedDraft(server, keyOf);
  const savedKey = useRef(serverKey);
  useEffect(() => {
    savedKey.current = serverKey;
  }, [serverKey]);

  useEffect(() => {
    if (!autoFocus) return;
    titleRef.current?.focus();
    titleRef.current?.select();
  }, [autoFocus]);

  const parsed = UpsertDecisionGroupInput.safeParse({
    groupId: group.id,
    code: draft.code,
    title: draft.title,
    note: draft.note.trim() === '' ? null : draft.note,
    highlight: draft.highlight.trim() === '' ? null : draft.highlight,
  });
  const duplicate = draft.code.trim() !== '' && otherCodes.includes(draft.code.trim());

  const upsert = useMutation({
    mutationFn: (variables: { readonly input: UpsertDecisionGroupInput; readonly key: string }) =>
      decisionSheetApi.upsertGroup(sheetId, variables.input),
    onSuccess: (_result, variables) => {
      savedKey.current = variables.key;
      void queryClient.invalidateQueries({ queryKey: ['decision-sheet', sheetId] });
    },
  });

  const save = (): void => {
    if (!parsed.success || duplicate || upsert.isPending) return;
    const key = keyOf(draft);
    if (key === savedKey.current) return;
    upsert.mutate({ input: parsed.data, key });
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget as Node | null;
    if (next && ref.current?.contains(next)) return;
    setTouched(true);
    save();
  };

  const set = (patch: Partial<typeof server>): void => setDraft((d) => ({ ...d, ...patch }));
  const invalid = touched && (!parsed.success || duplicate);

  return (
    <div
      ref={ref}
      onBlur={onBlur}
      className="group/header mt-6 flex min-w-0 flex-col gap-1 border-b-2 border-foreground pb-2"
      data-testid={`decision-group-editor-${group.code}`}
    >
      <div className="flex flex-wrap items-start gap-2">
        <span className="mt-0.5">
          <CodeChip
            tone="solid"
            value={draft.code}
            invalid={touched && (duplicate || draft.code.trim() === '') ? true : undefined}
            label={t('decisionSheet.editor.groupCode')}
            editLabel={t('decisionSheet.editor.groupCodeEdit', { code: draft.code })}
            onChange={(code) => set({ code })}
          />
        </span>
        <div className="min-w-0 flex-1 basis-40">
          <InlineText
            ref={titleRef}
            aria-label={t('decisionSheet.editor.groupTitle')}
            placeholder={t('decisionSheet.editor.groupTitle')}
            value={draft.title}
            maxLength={300}
            aria-invalid={touched && draft.title.trim() === '' ? true : undefined}
            onValueChange={(title) => set({ title })}
            className="text-xl font-bold leading-7 tracking-tight"
          />
        </div>
        <HighlightField value={draft.highlight} onChange={(highlight) => set({ highlight })} />
        <SaveState
          pending={upsert.isPending}
          failed={upsert.isError && !upsert.isPending}
          error={upsert.error}
          saved={upsert.isSuccess && keyOf(draft) === savedKey.current}
          unsaved={invalid}
          onRetry={save}
        />
        <button
          type="button"
          disabled={!empty}
          aria-label={t('decisionSheet.editor.deleteGroupCode', { code: group.code })}
          title={
            empty ? t('decisionSheet.editor.deleteGroup') : t('decisionSheet.editor.groupNotEmpty')
          }
          onClick={onDelete}
          className={cn(
            'inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-[color,background-color,opacity] hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground max-sm:size-10',
            empty ? HEADER_REVEAL : 'opacity-0 group-hover/header:opacity-40',
          )}
        >
          <Trash2 aria-hidden className="size-4" />
        </button>
      </div>
      <InlineText
        multiline
        aria-label={t('decisionSheet.editor.groupNote')}
        placeholder={t('decisionSheet.editor.groupNoteHint')}
        value={draft.note}
        maxLength={5_000}
        onValueChange={(note) => set({ note })}
        className="text-sm text-muted-foreground"
      />
      {touched && duplicate ? (
        <p role="alert" className="text-xs text-destructive">
          {t('decisionSheet.editor.duplicateGroup', { code: draft.code.trim() })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Nhãn nổi bật của nhóm ("Ảnh hưởng KPI / tiền") — viên đỏ như chế độ Xem,
 * sửa ngay trong viên. Chưa có thì là một nút "+ Nhãn nổi bật" hiện khi hover.
 */
function HighlightField({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (value === '' && !open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'relative mt-0.5 inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-dashed border-border px-2 text-xs text-muted-foreground transition-[color,opacity] after:absolute after:-inset-2 after:content-[""] hover:text-foreground',
          HEADER_REVEAL,
        )}
      >
        <Plus aria-hidden className="size-3" />
        {t('decisionSheet.editor.highlight')}
      </button>
    );
  }
  return (
    <input
      autoFocus={open}
      aria-label={t('decisionSheet.editor.highlight')}
      placeholder={t('decisionSheet.editor.highlightHint')}
      value={value}
      maxLength={60}
      onChange={(event) => onChange(event.target.value)}
      onBlur={() => setOpen(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      // Rộng theo chữ — viên co giãn như nhãn ở chế độ Xem.
      style={{ width: `${Math.max(value.length, 12) + 3}ch` }}
      className="mt-0.5 h-6 max-w-full shrink-0 rounded-full border border-transparent bg-rag-red/15 px-2 text-xs font-medium text-rag-red transition-colors placeholder:font-normal placeholder:text-rag-red/60 hover:border-rag-red/40 focus:border-rag-red focus:outline-none"
    />
  );
}

/**
 * Danh sách phẳng kéo thả được (`@dnd-kit`, cùng thư viện với sắp tài liệu
 * `document-order.tsx`). Tiêu đề nhóm là mốc, không kéo được; câu mới chưa lưu
 * cũng không.
 *
 * Thứ tự kéo xong giữ cục bộ cho tới khi server trả thứ tự mới — **không**
 * dựng lại cả danh sách bằng `key` (cách cũ): dựng lại là mọi thẻ đang sửa dở
 * mất bản nháp và ô đang gõ mất tiêu điểm.
 */
function SortableRows({
  rows: serverRows,
  rowKey,
  disabled,
  onReorder,
  renderRow,
}: {
  readonly rows: readonly string[];
  readonly rowKey: (rowId: string) => string;
  readonly disabled: boolean;
  readonly onReorder: (rows: readonly string[]) => void;
  readonly renderRow: (
    rowId: string,
    index: number,
    rows: readonly string[],
  ) => { readonly body: ReactNode; readonly footer: ReactNode };
}): ReactNode {
  const base = serverRows.join('|');
  const [local, setLocal] = useState<{ base: string; rows: readonly string[] } | null>(null);
  const rows = local && local.base === base ? local.rows : serverRows;
  const [dragging, setDragging] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent): void => {
    setDragging(false);
    if (!over || active.id === over.id) return;
    const from = rows.indexOf(String(active.id));
    const to = rows.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove([...rows], from, to);
    setLocal({ base, rows: next });
    onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={() => setDragging(true)}
      onDragCancel={() => setDragging(false)}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={[...rows]} strategy={verticalListSortingStrategy}>
        <ul className="flex flex-col gap-2">
          {rows.map((rowId, index) => {
            const { body, footer } = renderRow(rowId, index, rows);
            return (
              <SortableRow
                key={rowKey(rowId)}
                id={rowId}
                draggable={!disabled && !isGroupRow(rowId) && !isDraftRow(rowId)}
                footer={footer}
                // Nút "+ Câu hỏi" giữ chỗ nhưng ẩn khi đang kéo — đổi bố cục giữa lúc kéo làm lệch chỗ thả.
                hideFooter={dragging}
              >
                {body}
              </SortableRow>
            );
          })}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  draggable,
  footer,
  hideFooter,
  children,
}: {
  readonly id: string;
  readonly draggable: boolean;
  readonly footer: ReactNode;
  readonly hideFooter: boolean;
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const header = isGroupRow(id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    // Tiêu đề nhóm vẫn là chỗ thả (để kéo câu xuống dưới nó), chỉ không tự kéo được.
    disabled: { draggable: !draggable, droppable: false },
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={header ? `decision-editor-${id}` : `decision-editor-question-${id}`}
      className={cn('flex min-w-0 flex-col gap-1', isDragging && 'relative z-10 opacity-90')}
    >
      <div className="flex min-w-0 items-start gap-1">
        {header ? null : draggable ? (
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={t('decisionSheet.editor.dragHandle')}
            className="mt-3 flex h-10 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing max-sm:w-7"
          >
            <GripVertical aria-hidden className="size-4" />
          </button>
        ) : (
          <span aria-hidden className="w-6 shrink-0 max-sm:w-7" />
        )}
        <div className={cn('min-w-0 flex-1', isDragging && 'shadow-lg')}>{children}</div>
      </div>
      {footer ? (
        <div className={cn('flex', header ? null : 'pl-7 max-sm:pl-8', hideFooter && 'invisible')}>
          {footer}
        </div>
      ) : null}
    </li>
  );
}
